import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const SystemNotifications: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<any>({});
  const [previewType, setPreviewType] = useState<'payment_created' | 'payment_confirmed' | 'payment_expired' | 'withdrawal_created' | 'withdrawal_confirmed' | 'withdrawal_rejected' | 'withdrawal_processed'>('withdrawal_created');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/owner/system/alerts', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
        const json = await res.json();
        setData(json || {});
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async (patch: any) => {
    setSaving(true);
    try {
      const res = await fetch('/api/owner/system/alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(patch)
      });
      const json = await res.json();
      if (!res.ok || json?.error) throw new Error(json?.error || 'save failed');
      // optimistic local merge
      setData((d: any) => ({ ...d, ...patch }));
    } catch (e: any) {
      alert(e.message || 'Ошибка сохранения');
    } finally { setSaving(false); }
  };

  const previewHtml = useMemo(() => {
    const showShop = !!data.telegram_default_msg_show_shop_name;
    const showDates = !!data.telegram_default_msg_show_dates;
    const showStatus = !!data.telegram_default_msg_show_status;
    const showWarn = !!data.telegram_default_msg_show_warning;
    const emoji = {
      created: data.telegram_default_emoji_created || '🆕',
      searching: data.telegram_default_emoji_searching || '🔍',
      success: data.telegram_default_emoji_success || '✅',
      failure: data.telegram_default_emoji_failure || '❌',
      expired: data.telegram_default_emoji_expired || '⏰',
      withdrawal: data.telegram_default_emoji_withdrawal || '💸'
    };
    const now = new Date();
    const fmt = (d: Date) => showDates ? d.toLocaleString('ru-RU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
    const sample = {
      id: 12345,
      amount: 1000,
      reservedAmount: 1003,
      currency: 'RUB',
      customerName: 'demo_user',
      senderName: 'Иван Иванов',
      shopName: showShop ? 'Demo Shop' : '',
      createdAt: fmt(now),
      expiresAt: fmt(new Date(now.getTime() + 10*60*1000)),
      processedBy: 'admin',
      processedByRole: 'admin',
      processedAt: fmt(now),
      requisites: '**** 1234'
    };
    // Match notification-service formatting: show reserved amount in parentheses when it differs.
    let amountWithReserve = `${sample.amount} ${sample.currency}`;
    if ((sample as any).reservedAmount && (sample as any).reservedAmount !== sample.amount) {
      amountWithReserve = `${sample.amount} ${sample.currency} (резерв: ${(sample as any).reservedAmount} ${sample.currency})`;
    }
    const withTitle = (title: string) => `<b>${title}</b>`;
    if (previewType.startsWith('payment')) {
      const head = previewType === 'payment_created' ? `${emoji.created} ${withTitle(`Новый платеж #${sample.id}`)}`
        : previewType === 'payment_confirmed' ? `${emoji.success} ${withTitle(`Платеж #${sample.id} - ПОДТВЕРЖДЕН`)}`
        : `${emoji.expired} ${withTitle(`Платеж #${sample.id} - ИСТЕК`)}`;
      const lines = [head, '', `💰 <b>Сумма:</b> ${amountWithReserve}`, `👤 <b>Покупатель:</b> ${sample.customerName}`];
      if (showShop) lines.push(`🏪 <b>Магазин:</b> ${sample.shopName}`);
      if (previewType === 'payment_created' && showDates) lines.push(`📅 <b>Создан:</b> ${sample.createdAt}`, `⏰ <b>Истекает:</b> ${sample.expiresAt}`);
      if (previewType === 'payment_confirmed') {
        // Sender line is shown in real template only when senderName is available.
        if ((sample as any).senderName) lines.push(`💳 <b>Отправитель:</b> ${(sample as any).senderName}`);
        lines.push(`👨‍💼 <b>Обработал:</b> ${sample.processedBy} (администратор)`, showDates ? `📅 <b>Подтвержден:</b> ${sample.processedAt}` : '');
      }
      if (previewType === 'payment_expired' && showDates) lines.push(`📅 <b>Истек:</b> ${sample.expiresAt}`);
      return lines.filter(Boolean).join('\n');
    }
    // withdrawals
    const title = (data.telegram_default_text_withdrawal_created_title || 'НОВЫЙ ЗАПРОС НА ВЫВОД');
    const statusText = (data.telegram_default_text_withdrawal_status_text || '📋 <b>Статус:</b> Требует обработки оператором');
    const warnText = (data.telegram_default_text_withdrawal_warning || '⚠️ <i>Требуется подтверждение или отклонение</i>');
    let head = `${emoji.withdrawal} ${withTitle(`${title} #${sample.id}`)}`;
    if (previewType === 'withdrawal_confirmed') head = `${emoji.success} ${withTitle(`ВЫВОД #${sample.id} - ПОДТВЕРЖДЕН`)}`;
    if (previewType === 'withdrawal_rejected') head = `${emoji.failure} ${withTitle(`ВЫВОД #${sample.id} - ОТКЛОНЕН`)}`;
    if (previewType === 'withdrawal_processed') head = `${emoji.withdrawal} ${withTitle(`ВЫВОД #${sample.id} - ВЫПОЛНЕН`)}`;
    const parts = [head, '', `💰 <b>Сумма:</b> ${sample.amount} ${sample.currency}`, `👤 <b>Клиент:</b> ${sample.customerName}`];
    if (showShop) parts.push(`🏪 <b>Магазин:</b> ${sample.shopName}`);
    parts.push(`💳 <b>Реквизиты:</b> ${sample.requisites}`);
    if (showDates) parts.push(`📅 <b>Создан:</b> ${sample.createdAt}`);
    if (showStatus) parts.push(statusText);
    if (showWarn) parts.push(warnText);
    return parts.filter(Boolean).join('\n');
  }, [data, previewType]);

  if (user?.role !== 'owner') {
    return <div className="text-center text-red-600">Доступ запрещен</div>;
  }
  if (loading) return <div className="text-center">Загрузка...</div>;

  return (
    <div className="max-w-5xl mx-auto px-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Глобальные шаблоны Telegram</h2>

      <div className="bg-white p-6 rounded-lg shadow-md space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <label className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Показывать название магазина</span>
              <input type="checkbox" className="h-4 w-4" checked={!!data.telegram_default_msg_show_shop_name} onChange={e => { const v = e.target.checked; setData((d: any) => ({ ...d, telegram_default_msg_show_shop_name: v })); save({ telegram_default_msg_show_shop_name: v }); }} />
            </label>
            <label className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Показывать даты/время</span>
              <input type="checkbox" className="h-4 w-4" checked={!!data.telegram_default_msg_show_dates} onChange={e => { const v = e.target.checked; setData((d: any) => ({ ...d, telegram_default_msg_show_dates: v })); save({ telegram_default_msg_show_dates: v }); }} />
            </label>
            <label className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Показывать статус</span>
              <input type="checkbox" className="h-4 w-4" checked={!!data.telegram_default_msg_show_status} onChange={e => { const v = e.target.checked; setData((d: any) => ({ ...d, telegram_default_msg_show_status: v })); save({ telegram_default_msg_show_status: v }); }} />
            </label>
            <label className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Показывать предупреждение</span>
              <input type="checkbox" className="h-4 w-4" checked={!!data.telegram_default_msg_show_warning} onChange={e => { const v = e.target.checked; setData((d: any) => ({ ...d, telegram_default_msg_show_warning: v })); save({ telegram_default_msg_show_warning: v }); }} />
            </label>
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">Эмодзи по умолчанию</div>
            {[
              { key: 'telegram_default_emoji_created', label: 'Создан' },
              { key: 'telegram_default_emoji_searching', label: 'Поиск' },
              { key: 'telegram_default_emoji_success', label: 'Успех' },
              { key: 'telegram_default_emoji_failure', label: 'Отказ' },
              { key: 'telegram_default_emoji_expired', label: 'Истёк' },
              { key: 'telegram_default_emoji_withdrawal', label: 'Вывод' }
            ].map(it => (
              <div key={it.key} className="flex items-center justify-between">
                <label className="text-sm text-gray-700 mr-2">{it.label}</label>
                <input type="text" className="w-24 border border-gray-300 rounded px-2 py-1 text-center" value={data[it.key] || ''} onChange={e => { const v = e.target.value; setData((d: any) => ({ ...d, [it.key]: v })); save({ [it.key]: v }); }} placeholder="😀" />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-700">Тексты (вывод средств)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-700">Заголовок “новый запрос на вывод”</label>
              <input type="text" className="mt-1 block w-full border border-gray-300 rounded px-3 py-2" value={data.telegram_default_text_withdrawal_created_title || ''} onChange={e => { const v = e.target.value; setData((d: any) => ({ ...d, telegram_default_text_withdrawal_created_title: v })); save({ telegram_default_text_withdrawal_created_title: v }); }} placeholder="НОВЫЙ ЗАПРОС НА ВЫВОД" />
            </div>
            <div>
              <label className="block text-sm text-gray-700">Строка статуса</label>
              <input type="text" className="mt-1 block w-full border border-gray-300 rounded px-3 py-2" value={data.telegram_default_text_withdrawal_status_text || ''} onChange={e => { const v = e.target.value; setData((d: any) => ({ ...d, telegram_default_text_withdrawal_status_text: v })); save({ telegram_default_text_withdrawal_status_text: v }); }} placeholder="📋 Статус: Требует обработки оператором" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-700">Предупреждение</label>
              <input type="text" className="mt-1 block w-full border border-gray-300 rounded px-3 py-2" value={data.telegram_default_text_withdrawal_warning || ''} onChange={e => { const v = e.target.value; setData((d: any) => ({ ...d, telegram_default_text_withdrawal_warning: v })); save({ telegram_default_text_withdrawal_warning: v }); }} placeholder="⚠️ Требуется подтверждение или отклонение" />
            </div>
          </div>
        </div>

        {/* Предпросмотр сообщения */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-gray-700">Предпросмотр Telegram-сообщения</div>
            <select
              value={previewType}
              onChange={e => setPreviewType(e.target.value as any)}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value="payment_created">Платеж: создан</option>
              <option value="payment_confirmed">Платеж: подтвержден</option>
              <option value="payment_expired">Платеж: истек</option>
              <option value="withdrawal_created">Вывод: создан</option>
              <option value="withdrawal_confirmed">Вывод: подтвержден</option>
              <option value="withdrawal_rejected">Вывод: отклонен</option>
              <option value="withdrawal_processed">Вывод: выполнен</option>
            </select>
          </div>
          <div className="p-4 bg-gray-50 border rounded text-sm" style={{whiteSpace: 'pre-wrap'}} dangerouslySetInnerHTML={{ __html: previewHtml }} />
          <div className="text-xs text-gray-500">Предпросмотр учитывает текущие глобальные значения флагов, эмодзи и текстов.</div>
        </div>

        <div className="flex justify-end">
          <button onClick={() => save(data)} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">{saving ? 'Сохранение...' : 'Сохранить'}</button>
        </div>
      </div>
    </div>
  );
};

export default SystemNotifications;


